import { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '@/utils/logger';
import { storage } from '@/utils/storage';

interface PersistentStateOptions {
  syncAcrossTabs?: boolean;
  debounceMs?: number;
  onRestore?: (value: any) => void;
  immediate?: boolean; // Skip debouncing for critical saves
}

export function usePersistentState<T>(
  key: string,
  defaultValue: T,
  options: PersistentStateOptions = {}
): [T, (value: T | ((prev: T) => T)) => void] {
  const {
    syncAcrossTabs = true,
    debounceMs = 100,
    onRestore,
    immediate = false
  } = options;

  // Track if we're mounted to prevent hydration issues
  const [isMounted, setIsMounted] = useState(false);
  
  // Initialize state from storage or default
  const [state, setState] = useState<T>(() => {
    // During SSR, always return default value
    if (typeof window === 'undefined') return defaultValue;
    
    // Return default value initially, we'll restore from storage in useEffect
    return defaultValue;
  });
  
  // Set mounted flag after hydration
  useEffect(() => {
    setIsMounted(true);
    
    // Sync with storage after mount to handle hydration
    const restoreFromStorage = async () => {
      try {
        const item = await storage.getItem(key, {
          useSessionStorage: true,
          useIndexedDB: true,
          useCookies: false
        });
        
        if (item) {
          const parsed = JSON.parse(item);
          const currentSerialized = JSON.stringify(state);
          
          // Only update if storage value is different
          if (item !== currentSerialized) {
            logger.debug('usePersistentState', `Post-hydration sync for ${key}`, parsed);
            setState(parsed);
            lastValueRef.current = item;
          }
        }
      } catch (error) {
        logger.error('usePersistentState', `Error during post-hydration sync for ${key}`, error);
      }
    };
    
    restoreFromStorage();
  }, []);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastValueRef = useRef<string>(JSON.stringify(state));
  const isRestoringRef = useRef(false);

  // Save to storage with retry logic
  const saveToStorageWithRetry = useCallback(async (serialized: string, retries = 3): Promise<boolean> => {
    try {
      const success = await storage.setItem(key, serialized, {
        useSessionStorage: true,
        useIndexedDB: true,
        useCookies: false,
        maxRetries: retries
      });
      
      if (success) {
        lastValueRef.current = serialized;
        logger.debug('usePersistentState', `Saved ${key} to storage`);
        return true;
      } else {
        logger.error('usePersistentState', `Failed to save ${key} to any storage method`);
        return false;
      }
    } catch (error) {
      logger.error('usePersistentState', `Error saving ${key} to storage`, error);
      return false;
    }
  }, [key]);

  // Save to storage with optional debouncing
  const saveToStorage = useCallback((value: T, forceImmediate = false) => {
    const serialized = JSON.stringify(value);
    
    // Only save if value actually changed
    if (serialized === lastValueRef.current) {
      return;
    }

    // Immediate save for critical operations
    if (immediate || forceImmediate) {
      saveToStorageWithRetry(serialized);
      return;
    }

    // Debounced save for normal operations
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      saveToStorageWithRetry(serialized);
    }, debounceMs);
  }, [immediate, debounceMs, saveToStorageWithRetry]);

  // Custom setState that also saves to storage
  const setPersistentState = useCallback((value: T | ((prev: T) => T)) => {
    setState((prevState) => {
      const newValue = typeof value === 'function' 
        ? (value as (prev: T) => T)(prevState)
        : value;
      
      // Save to storage only after mount to prevent hydration issues
      if (typeof window !== 'undefined' && !isRestoringRef.current && isMounted) {
        saveToStorage(newValue);
      }
      
      return newValue;
    });
  }, [saveToStorage, isMounted]);

  // Handle visibility change and page hide
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        logger.debug('usePersistentState', `Tab became visible, checking ${key} in storage`);
        isRestoringRef.current = true;
        
        try {
          const item = await storage.getItem(key, {
            useSessionStorage: true,
            useIndexedDB: true,
            useCookies: false
          });
          
          if (item) {
            const parsed = JSON.parse(item);
            const currentSerialized = JSON.stringify(state);
            
            // Only update if storage value is different
            if (item !== currentSerialized) {
              logger.info('usePersistentState', `Restoring ${key} from storage after tab return`);
              setState(parsed);
              lastValueRef.current = item;
              
              if (onRestore) {
                onRestore(parsed);
              }
            }
          }
        } catch (error) {
          logger.error('usePersistentState', `Error restoring ${key} from storage`, error);
        } finally {
          // Reset flag after a short delay
          setTimeout(() => {
            isRestoringRef.current = false;
          }, 100);
        }
      } else if (document.visibilityState === 'hidden') {
        // Save immediately when tab is being hidden
        logger.debug('usePersistentState', `Tab becoming hidden, saving ${key} immediately`);
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        saveToStorage(state, true); // Force immediate save
      }
    };

    const handlePageHide = () => {
      // Page is being unloaded or hidden (works better on mobile)
      logger.debug('usePersistentState', `Page hide event, saving ${key} immediately`);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      const serialized = JSON.stringify(state);
      // Use synchronous localStorage save for pagehide (fastest option)
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(key, serialized);
        }
      } catch (error) {
        logger.error('usePersistentState', `Error saving ${key} on page hide`, error);
      }
    };

    const handleBeforeUnload = () => {
      // Last chance to save before page unload
      logger.debug('usePersistentState', `Before unload event, saving ${key}`);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      const serialized = JSON.stringify(state);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(key, serialized);
        }
      } catch (error) {
        // Can't log here as page is unloading
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [key, state, onRestore, saveToStorage]);

  // Handle storage events for cross-tab synchronization
  useEffect(() => {
    if (!syncAcrossTabs || typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue);
          logger.info('usePersistentState', `Cross-tab sync: ${key} changed in another tab`);
          isRestoringRef.current = true;
          setState(newValue);
          lastValueRef.current = e.newValue;
          
          if (onRestore) {
            onRestore(newValue);
          }
          
          setTimeout(() => {
            isRestoringRef.current = false;
          }, 100);
        } catch (error) {
          logger.error('usePersistentState', `Error syncing ${key} from another tab`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, syncAcrossTabs, onRestore]);

  // Save current state on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Force save on unmount using localStorage for speed
        try {
          const serialized = JSON.stringify(state);
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(key, serialized);
            logger.debug('usePersistentState', `Saved ${key} on unmount`);
          }
        } catch (error) {
          logger.error('usePersistentState', `Error saving ${key} on unmount`, error);
        }
      }
    };
  }, [key, state]);

  return [state, setPersistentState];
}

// Helper hook for complex objects with partial updates
export function usePersistentReducer<T extends Record<string, any>>(
  key: string,
  defaultValue: T,
  options: PersistentStateOptions = {}
): [T, (updates: Partial<T>) => void, () => void] {
  const [state, setState] = usePersistentState<T>(key, defaultValue, options);

  const updateState = useCallback((updates: Partial<T>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, [setState]);

  const resetState = useCallback(() => {
    setState(defaultValue);
  }, [setState, defaultValue]);

  return [state, updateState, resetState];
}